import { ArrowLeft, CalendarClock, Eye, GripVertical, Plus, Send, SendHorizonal, Trash2, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { previewInvitation, useCustomers, useEvent, useInvitation, useInvitationAction, useRecipients, useSends, useUpdateInvitation, useVariables } from '../../api/hooks'
import type { Block, Invitation, Preview } from '../../api/types'
import { Badge, Button, Card, ErrorNote, Field, Input, Modal, Select, Spinner, Textarea, Toggle } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { fmtDateTime, fromLocalInput, INV_STATUS_LABEL, num, pct, SEND_STATUS_LABEL } from '../../lib/format'

type Draft = Pick<Invitation, 'title' | 'subject' | 'preheader' | 'sender_name' | 'sender_email' | 'reply_to' | 'blocks' | 'include_kund' | 'include_prospekt' | 'reminder_days_before'>

const BLOCK_LABEL: Record<Block['type'], string> = { heading: 'Rubrik', text: 'Textblock', event_details: 'Eventdetaljer', cta: 'Knapp (anmälningslänk)', divider: 'Avdelare', image: 'Bild' }

export function InvitationEditorPage() {
  const { id: eventId = '', invitationId = '' } = useParams()
  const ev = useEvent(eventId)
  const inv = useInvitation(invitationId)
  if (ev.isLoading || inv.isLoading) return <Spinner />
  if (!ev.data || !inv.data) return <div className="card p-6 text-sm text-ink-500">Inbjudan kunde inte hämtas.</div>
  return <Editor key={inv.data.id + inv.data.status} eventId={eventId} eventName={ev.data.name} invitation={inv.data} />
}

function Editor({ eventId, eventName, invitation: inv }: { eventId: string; eventName: string; invitation: Invitation }) {
  const readOnly = inv.status === 'SKICKAS' || inv.status === 'SKICKAT'
  const update = useUpdateInvitation(inv.id, eventId)
  const actions = useInvitationAction(inv.id, eventId)
  const recipients = useRecipients(inv.id)
  const variables = useVariables()
  const customers = useCustomers('')
  const sends = useSends(inv.id, readOnly)
  const toast = useToast()

  const [d, setD] = useState<Draft>({ title: inv.title, subject: inv.subject, preheader: inv.preheader, sender_name: inv.sender_name, sender_email: inv.sender_email, reply_to: inv.reply_to, blocks: inv.blocks, include_kund: inv.include_kund, include_prospekt: inv.include_prospekt, reminder_days_before: inv.reminder_days_before })
  const [dirty, setDirty] = useState(false)
  const [previewCustomer, setPreviewCustomer] = useState<string>('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [mode, setMode] = useState<'html' | 'text'>('html')
  const [testOpen, setTestOpen] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [confirmSend, setConfirmSend] = useState(false)
  const timer = useRef<number | null>(null)

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => { setD((s) => ({ ...s, [k]: v })); setDirty(true) }

  // Live preview: debounce editor state -> server render. Same renderer as the real send.
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      previewInvitation(inv.id, { customer_id: previewCustomer || null, subject: d.subject, preheader: d.preheader, blocks: d.blocks }).then(setPreview).catch(() => undefined)
    }, 350)
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [d.subject, d.preheader, d.blocks, previewCustomer, inv.id])

  // Autosave 1.2 s after last change.
  useEffect(() => {
    if (!dirty || readOnly) return
    const t = window.setTimeout(() => update.mutate({ ...d }, { onSuccess: () => setDirty(false) }), 1200)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, dirty])

  const blocks = d.blocks
  const updateBlock = (i: number, b: Block) => set('blocks', blocks.map((x, j) => (j === i ? b : x)))
  const removeBlock = (i: number) => set('blocks', blocks.filter((_, j) => j !== i))
  const moveBlock = (i: number, dir: -1 | 1) => { const n = [...blocks]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set('blocks', n) }
  const addBlock = (type: Block['type']) => {
    const nb: Block = type === 'heading' ? { type, text: 'Rubrik' } : type === 'text' ? { type, text: '' } : type === 'cta' ? { type, label: 'Anmäl dig här' } : type === 'image' ? { type, url: '', alt: '' } : { type } as Block
    set('blocks', [...blocks, nb])
  }

  const sample = useMemo(() => customers.data?.slice(0, 40) ?? [], [customers.data])
  const rec = recipients.data

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/event/${eventId}/inbjudningar`} className="rounded-md p-1.5 text-ink-500 hover:bg-navy-50 hover:text-ink-900"><ArrowLeft className="size-4" /></Link>
          <div>
            <div className="text-xs text-ink-500">{eventName}</div>
            {readOnly ? <h1 className="text-lg">{d.title}</h1> : <input value={d.title} onChange={(e) => set('title', e.target.value)} className="-ml-1 rounded px-1 text-lg font-semibold text-ink-900 hover:bg-navy-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-navy-700/25" />}
          </div>
          <Badge tone={inv.status === 'SKICKAT' ? 'success' : inv.status === 'SCHEMALAGD' ? 'warning' : 'neutral'} dot>{INV_STATUS_LABEL[inv.status]}</Badge>
          {!readOnly && <span className="text-xs text-ink-400">{update.isPending ? 'Sparar…' : dirty ? 'Osparade ändringar' : 'Sparat'}</span>}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button size="sm" icon={<Send className="size-4" />} onClick={() => setTestOpen(true)}>Skicka test</Button>
            {inv.status === 'SCHEMALAGD' ? (
              <Button size="sm" onClick={() => actions.unschedule.mutate(undefined, { onSuccess: () => toast.push('info', 'Schemaläggningen avbröts') })}>Avbryt schemaläggning</Button>
            ) : (
              <Button size="sm" icon={<CalendarClock className="size-4" />} onClick={() => setScheduleOpen(true)}>Schemalägg</Button>
            )}
            <Button size="sm" variant="primary" icon={<SendHorizonal className="size-4" />} onClick={() => setConfirmSend(true)} disabled={!rec?.total}>Skicka till {rec ? num(rec.total) : '…'}</Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ---------- Editor ---------- */}
        <div className="space-y-4">
          {readOnly ? (
            <Card title="Resultat">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-ink-500">Skickade</div><div className="text-xl font-semibold tabular-nums">{num(inv.sent_count)}</div></div>
                <div><div className="text-xs text-ink-500">Öppnade</div><div className="text-xl font-semibold tabular-nums">{inv.sent_count ? pct(inv.opened_count / inv.sent_count) : '–'}</div></div>
                <div><div className="text-xs text-ink-500">Klickade</div><div className="text-xl font-semibold tabular-nums">{inv.sent_count ? pct(inv.clicked_count / inv.sent_count) : '–'}</div></div>
                <div><div className="text-xs text-ink-500">Anmälda</div><div className="text-xl font-semibold tabular-nums text-navy-800">{num(inv.registered_count)}</div></div>
              </div>
              {inv.sent_at && <p className="mt-3 text-xs text-ink-500">Skickat {fmtDateTime(inv.sent_at)} från {inv.sender_name} &lt;{inv.sender_email}&gt;</p>}
            </Card>
          ) : (
            <Card title="Avsändare & ämne">
              <div className="grid gap-4">
                <Field label="Ämnesrad"><Input value={d.subject} onChange={(e) => set('subject', e.target.value)} /></Field>
                <Field label="Förhandstext" hint="Visas efter ämnesraden i inkorgen."><Input value={d.preheader ?? ''} onChange={(e) => set('preheader', e.target.value || null)} /></Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Avsändarnamn"><Input value={d.sender_name} onChange={(e) => set('sender_name', e.target.value)} /></Field>
                  <Field label="Avsändaradress"><Input type="email" value={d.sender_email} onChange={(e) => set('sender_email', e.target.value)} /></Field>
                </div>
              </div>
            </Card>
          )}

          {!readOnly && (
            <Card title="Innehåll" actions={<span className="text-xs text-ink-500">{blocks.length} block</span>}>
              <div className="space-y-3">
                {blocks.map((b, i) => (
                  <div key={i} className="group rounded-lg border border-line-200 bg-white">
                    <div className="flex items-center justify-between border-b border-line-100 px-3 py-1.5">
                      <div className="flex items-center gap-2 text-xs font-medium text-ink-500"><GripVertical className="size-3.5 text-ink-300" />{BLOCK_LABEL[b.type]}</div>
                      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                        <button className="rounded p-1 text-ink-400 hover:bg-navy-50 hover:text-ink-900 disabled:opacity-30" disabled={i === 0} onClick={() => moveBlock(i, -1)} title="Flytta upp">↑</button>
                        <button className="rounded p-1 text-ink-400 hover:bg-navy-50 hover:text-ink-900 disabled:opacity-30" disabled={i === blocks.length - 1} onClick={() => moveBlock(i, 1)} title="Flytta ned">↓</button>
                        <button className="rounded p-1 text-ink-400 hover:bg-danger-50 hover:text-danger-600" onClick={() => removeBlock(i)} title="Ta bort"><Trash2 className="size-3.5" /></button>
                      </div>
                    </div>
                    <div className="p-3">
                      {b.type === 'heading' && <Input value={b.text} onChange={(e) => updateBlock(i, { ...b, text: e.target.value })} className="font-semibold" />}
                      {b.type === 'text' && <Textarea value={b.text} onChange={(e) => updateBlock(i, { ...b, text: e.target.value })} placeholder="Skriv text… Använd variabler som {{förnamn}}." />}
                      {b.type === 'event_details' && <p className="text-xs text-ink-500">Datum, tid och plats hämtas automatiskt från eventet.</p>}
                      {b.type === 'cta' && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Field label="Knapptext"><Input value={b.label} onChange={(e) => updateBlock(i, { ...b, label: e.target.value })} /></Field>
                          <Field label="Länk" hint="Tom = mottagarens unika anmälningslänk."><Input value={b.url ?? ''} onChange={(e) => updateBlock(i, { ...b, url: e.target.value || undefined })} placeholder="{{anmälningslänk}}" /></Field>
                        </div>
                      )}
                      {b.type === 'image' && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Field label="Bild-URL"><Input value={b.url} onChange={(e) => updateBlock(i, { ...b, url: e.target.value })} placeholder="https://…" /></Field>
                          <Field label="Alt-text"><Input value={b.alt ?? ''} onChange={(e) => updateBlock(i, { ...b, alt: e.target.value })} /></Field>
                        </div>
                      )}
                      {b.type === 'divider' && <hr className="border-line-200" />}
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 pt-1">
                  {(Object.keys(BLOCK_LABEL) as Block['type'][]).map((t) => (
                    <Button key={t} size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => addBlock(t)}>{BLOCK_LABEL[t]}</Button>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {!readOnly && (
            <Card title="Mottagare" actions={<Users className="size-4 text-ink-400" />}>
              <div className="flex flex-wrap items-center gap-6">
                <Toggle checked={d.include_kund} onChange={(v) => set('include_kund', v)} label="Kunder" />
                <Toggle checked={d.include_prospekt} onChange={(v) => set('include_prospekt', v)} label="Prospekt" />
                <div className="ml-auto text-sm text-ink-500">{rec ? <><span className="font-semibold text-ink-900">{num(rec.total)}</span> mottagare · {rec.kund} kunder, {rec.prospekt} prospekt, {rec.teams} team</> : '…'}</div>
              </div>
              <p className="mt-3 text-xs text-ink-500">Avregistrerade kunder och team utan kategori på eventet utesluts automatiskt. Varje mottagare får sitt eget teams anmälningslänk.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Field label="Påminnelse till ej anmälda" hint="Skickas automatiskt X dagar före eventet (BÖR-krav F-INV-08).">
                  <Select value={d.reminder_days_before ?? ''} onChange={(e) => set('reminder_days_before', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">Ingen påminnelse</option>
                    {[3, 5, 7, 10, 14].map((n) => <option key={n} value={n}>{n} dagar före</option>)}
                  </Select>
                </Field>
              </div>
            </Card>
          )}

          {!readOnly && variables.data && (
            <Card title="Variabler">
              <div className="flex flex-wrap gap-1.5">
                {variables.data.map((v) => (
                  <button key={v.key} title={v.label} onClick={() => navigator.clipboard?.writeText(`{{${v.key}}}`).then(() => toast.push('info', `{{${v.key}}} kopierad`))} className="rounded-md border border-line-200 bg-white px-2 py-1 font-mono text-[11px] text-navy-800 hover:border-navy-700/40 hover:bg-navy-50">{`{{${v.key}}}`}</button>
                ))}
              </div>
              {preview && preview.unknown_variables.length > 0 && <p className="mt-3 text-xs text-danger-600">Okända variabler: {preview.unknown_variables.map((v) => `{{${v}}}`).join(', ')}</p>}
            </Card>
          )}

          {readOnly && (
            <Card padded={false} title="Mottagare" actions={<span className="text-xs text-ink-500">{sends.data?.length ?? 0} st</span>}>
              {sends.isLoading ? <Spinner /> : (
                <div className="max-h-[50vh] overflow-auto">
                  <table className="w-full min-w-[640px]">
                    <thead className="table-head sticky top-0 bg-card"><tr><th>Mottagare</th><th>Kategori</th><th>Status</th><th>Öppnad</th><th>Klick</th><th>Anmäld</th></tr></thead>
                    <tbody>
                      {sends.data?.map((s) => (
                        <tr key={s.id} className="table-row">
                          <td><div className="font-medium text-ink-900">{s.customer_name}</div><div className="text-xs text-ink-500">{s.to_email}</div></td>
                          <td className="font-mono text-xs">{s.category_name}</td>
                          <td><Badge tone={s.status === 'SKICKAT' ? 'success' : s.status === 'KO' ? 'neutral' : 'danger'}>{SEND_STATUS_LABEL[s.status]}</Badge></td>
                          <td className="text-xs tabular-nums text-ink-500">{s.opened_at ? fmtDateTime(s.opened_at) : '–'}</td>
                          <td className="text-xs tabular-nums text-ink-500">{s.clicked_at ? fmtDateTime(s.clicked_at) : '–'}</td>
                          <td>{s.registered_at ? <Badge tone="navy">Ja</Badge> : <span className="text-xs text-ink-400">–</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* ---------- Preview ---------- */}
        <div className="xl:sticky xl:top-20 xl:self-start">
          <Card padded={false} title={<div className="flex items-center gap-2"><Eye className="size-4 text-ink-400" /><h3 className="text-sm font-semibold">Förhandsgranskning</h3></div>} actions={
            <div className="flex items-center gap-2">
              <Select value={previewCustomer} onChange={(e) => setPreviewCustomer(e.target.value)} className="h-8 w-56 text-xs">
                <option value="">Exempelmottagare</option>
                {sample.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.team_code}</option>)}
              </Select>
              <div className="flex rounded-md border border-line-200 text-xs">
                <button onClick={() => setMode('html')} className={`px-2 py-1 ${mode === 'html' ? 'bg-navy-100 text-navy-800' : 'text-ink-500'}`}>HTML</button>
                <button onClick={() => setMode('text')} className={`px-2 py-1 ${mode === 'text' ? 'bg-navy-100 text-navy-800' : 'text-ink-500'}`}>Text</button>
              </div>
            </div>
          }>
            {preview ? (
              <>
                <div className="border-b border-line-100 px-4 py-2.5 text-sm">
                  <div className="text-xs text-ink-500">Från {d.sender_name} &lt;{d.sender_email}&gt;{preview.sample_customer && <> · Till {preview.sample_customer}</>}</div>
                  <div className="mt-0.5 font-medium text-ink-900">{preview.subject}</div>
                </div>
                {mode === 'html' ? (
                  <iframe title="Förhandsgranskning" srcDoc={preview.html} sandbox="" className="h-[70vh] w-full bg-page" />
                ) : (
                  <pre className="h-[70vh] overflow-auto whitespace-pre-wrap p-4 font-mono text-xs leading-relaxed text-ink-700">{preview.text}</pre>
                )}
              </>
            ) : <Spinner />}
          </Card>
        </div>
      </div>

      {/* ---------- Dialogs ---------- */}
      <Modal open={testOpen} onClose={() => setTestOpen(false)} title="Skicka testmail" footer={<><Button onClick={() => setTestOpen(false)}>Avbryt</Button><Button variant="primary" loading={actions.test.isPending} disabled={!testEmail} onClick={() => actions.test.mutate({ to_email: testEmail, customer_id: previewCustomer || null }, { onSuccess: () => { setTestOpen(false); toast.push('success', `Testmail skickat till ${testEmail} (se Skickade mejl)`) }, onError: (e) => toast.push('error', e.message) })}>Skicka</Button></>}>
        <div className="space-y-3">
          <p className="text-sm text-ink-500">Mejlet renderas som för {preview?.sample_customer ?? 'exempelmottagaren'} och skickas till adressen nedan med ämnet märkt [TEST].</p>
          <Field label="Till"><Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="ditt.namn@soderbergpartners.se" /></Field>
        </div>
      </Modal>

      <Modal open={scheduleOpen} onClose={() => setScheduleOpen(false)} title="Schemalägg utskick" footer={<><Button onClick={() => setScheduleOpen(false)}>Avbryt</Button><Button variant="primary" loading={actions.schedule.isPending} disabled={!scheduleAt} onClick={() => actions.schedule.mutate(fromLocalInput(scheduleAt)!, { onSuccess: () => { setScheduleOpen(false); toast.push('success', 'Utskicket är schemalagt') }, onError: (e) => toast.push('error', e.message) })}>Schemalägg</Button></>}>
        <div className="space-y-3">
          <Field label="Tidpunkt"><Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} /></Field>
          <ErrorNote error={actions.schedule.error} />
        </div>
      </Modal>

      <Modal open={confirmSend} onClose={() => setConfirmSend(false)} title="Skicka inbjudan nu?" footer={<><Button onClick={() => setConfirmSend(false)}>Avbryt</Button><Button variant="primary" loading={actions.send.isPending} onClick={() => actions.send.mutate(undefined, { onSuccess: () => { setConfirmSend(false); toast.push('success', 'Utskicket har startat') }, onError: (e) => toast.push('error', e.message) })}>Ja, skicka till {rec ? num(rec.total) : ''} mottagare</Button></>}>
        <div className="space-y-3 text-sm">
          <p>Inbjudan <strong>{d.title}</strong> skickas till <strong>{rec ? num(rec.total) : '…'} mottagare</strong> i {rec?.teams} team. Varje mottagare får sitt teams unika anmälningslänk.</p>
          {rec && rec.sample.length > 0 && <div className="rounded-lg bg-line-100 p-3 text-xs text-ink-500"><div className="mb-1 font-medium text-ink-700">Exempel på mottagare</div>{rec.sample.map((s) => <div key={s}>{s}</div>)}</div>}
          <p className="text-xs text-ink-500">Efter utskick låses inbjudan och eventets grunduppgifter (F-EV-02). Utskicket loggas i aktivitetsloggen.</p>
          <ErrorNote error={actions.send.error} />
        </div>
      </Modal>
    </>
  )
}
